import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import {
  ArrowLeft,
  Building2,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Euro,
  Target,
  History,
  Plus,
  Send,
  FileText,
  Users,
  StickyNote,
  Clock,
  Pencil,
  CheckCircle2,
  Copy,
  Loader2,
  Globe,
  ImageIcon,
} from "lucide-react";
import api from "../lib/api";

const STATUSES = [
  { value: "to_contact", label: "Da Contattare", color: "bg-slate-500" },
  { value: "contacted", label: "Contattato", color: "bg-blue-500" },
  { value: "presentation_sent", label: "Presentazione Inviata", color: "bg-purple-500" },
  { value: "catalog_sent", label: "Catalogo Inviato", color: "bg-indigo-500" },
  { value: "negotiation", label: "In Trattativa", color: "bg-orange-500" },
  { value: "won", label: "Vinto", color: "bg-green-500" },
  { value: "lost", label: "Perso", color: "bg-gray-500" },
];

const ACTIVITY_TYPES = [
  { value: "call", label: "Chiamata", icon: Phone },
  { value: "email_presentation", label: "Email Presentazione", icon: Send },
  { value: "email_catalog", label: "Invio Catalogo", icon: FileText },
  { value: "email_pricelist", label: "Invio Listino", icon: Euro },
  { value: "meeting", label: "Incontro", icon: Users },
  { value: "note", label: "Nota", icon: StickyNote },
  { value: "follow_up", label: "Follow-up", icon: Clock },
];

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [templates, setTemplates] = useState([]);
  
  // Dialogs
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  
  // Form states
  const [newActivity, setNewActivity] = useState({ type: "note", description: "" });
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [emailLanguage, setEmailLanguage] = useState("it");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    fetchLead();
    fetchActivities();
    fetchTemplates();
  }, [id]);

  const fetchLead = async () => {
    try {
      const response = await api.get(`/leads/${id}`);
      setLead(response.data);
      setEditForm(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento del lead");
      navigate("/leads");
    } finally {
      setLoading(false);
    }
  };

  const fetchActivities = async () => {
    try {
      const response = await api.get(`/leads/${id}/activities`);
      setActivities(response.data);
    } catch (error) {
      console.error("Error fetching activities:", error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.get("/email-templates");
      setTemplates(response.data);
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.put(`/leads/${id}`, { ...lead, status: newStatus });
      setLead({ ...lead, status: newStatus });
      toast.success("Stato aggiornato");
      fetchActivities();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleAddActivity = async () => {
    if (!newActivity.description.trim()) {
      toast.error("Inserisci una descrizione");
      return;
    }

    try {
      await api.post(`/leads/${id}/activities`, newActivity);
      toast.success("Attività registrata");
      setNewActivity({ type: "note", description: "" });
      setActivityDialogOpen(false);
      fetchActivities();
      fetchLead();
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleSendEmail = async () => {
    if (!selectedTemplate) {
      toast.error("Seleziona un template");
      return;
    }

    setSendingEmail(true);
    try {
      await api.post(`/leads/${id}/send-email`, {
        template_id: selectedTemplate,
        language: emailLanguage,
      });
      toast.success("Email inviata con successo!");
      setEmailDialogOpen(false);
      setSelectedTemplate(null);
      fetchActivities();
      fetchLead();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio dell'email");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleUpdateLead = async () => {
    try {
      await api.put(`/leads/${id}`, editForm);
      setLead(editForm);
      toast.success("Lead aggiornato");
      setEditDialogOpen(false);
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleConvert = async () => {
    if (!window.confirm("Convertire questo lead in cliente?")) return;
    
    try {
      await api.post(`/leads/${id}/convert`);
      toast.success("Lead convertito in cliente!");
      navigate("/clients");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella conversione");
    }
  };

  const copyLeadId = () => {
    navigator.clipboard.writeText(id);
    toast.success("ID copiato");
  };

  const getActivityIcon = (type) => {
    const found = ACTIVITY_TYPES.find(t => t.value === type);
    return found?.icon || StickyNote;
  };

  const getStatusInfo = (status) => {
    return STATUSES.find(s => s.value === status) || STATUSES[0];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!lead) return null;

  const statusInfo = getStatusInfo(lead.status);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/leads")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{lead.company_name}</h1>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium text-white ${statusInfo.color}`}>
                {statusInfo.label}
              </span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground mt-1">
              <code className="text-xs bg-muted px-2 py-1 rounded">{id}</code>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={copyLeadId}>
                <Copy className="w-3 h-3" />
              </Button>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setEditDialogOpen(true)}>
            <Pencil className="w-4 h-4 mr-2" />
            Modifica
          </Button>
          {lead.status !== "won" && lead.status !== "lost" && (
            <Button className="bg-green-600 hover:bg-green-700" onClick={handleConvert}>
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Converti in Cliente
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Info & Actions */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Informazioni Contatto
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <Label className="text-muted-foreground">Referente</Label>
                  <p className="font-medium">{lead.contact_name}</p>
                  {lead.contact_role && <p className="text-sm text-muted-foreground">{lead.contact_role}</p>}
                </div>
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <a href={`mailto:${lead.email}`} className="flex items-center gap-2 text-blue-600 hover:underline">
                    <Mail className="w-4 h-4" />
                    {lead.email}
                  </a>
                </div>
                <div>
                  <Label className="text-muted-foreground">Telefono</Label>
                  <a href={`tel:${lead.phone}`} className="flex items-center gap-2 text-blue-600 hover:underline">
                    <Phone className="w-4 h-4" />
                    {lead.phone}
                  </a>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <Label className="text-muted-foreground">Canale</Label>
                  <p className="font-medium">{lead.channel}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Fonte</Label>
                  <p className="font-medium">{lead.source_detail || lead.source}</p>
                </div>
                {lead.business_card && (
                  <div>
                    <Label className="text-muted-foreground">Biglietto da Visita</Label>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/fiere/business-cards/${lead.business_card}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-purple-600 hover:underline"
                    >
                      <ImageIcon className="w-4 h-4" />
                      Visualizza
                    </a>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Categories & Notes */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5" />
                Dettagli Lead
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {lead.interested_categories && lead.interested_categories.length > 0 && (
                <div>
                  <Label className="text-muted-foreground">Categorie di Interesse</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {lead.interested_categories.map((cat) => {
                      const labels = { milone_spirits: "Milone Spirits", belpaese: "Belpaese", conto_terzi: "Conto Terzi" };
                      return (
                        <span key={cat} className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                          {labels[cat] || cat}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
              <div>
                <Label className="text-muted-foreground">Stato Pipeline</Label>
                <Select value={lead.status} onValueChange={handleStatusChange}>
                  <SelectTrigger className="w-full md:w-64 mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${status.color}`} />
                          {status.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {lead.notes && (
                <div>
                  <Label className="text-muted-foreground">Note</Label>
                  <p className="mt-2 p-3 bg-muted rounded-lg text-sm">{lead.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Email Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="w-5 h-5" />
                Invia Comunicazione
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Seleziona un template per inviare un'email al lead. Puoi gestire i template dalla sezione Impostazioni.
              </p>
              <Button onClick={() => setEmailDialogOpen(true)} className="w-full md:w-auto">
                <Mail className="w-4 h-4 mr-2" />
                Invia Email con Template
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Activity Timeline */}
        <div className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Storico Attività
              </CardTitle>
              <Button size="sm" onClick={() => setActivityDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-1" />
                Aggiungi
              </Button>
            </CardHeader>
            <CardContent>
              {activities.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Nessuna attività registrata
                </p>
              ) : (
                <ScrollArea className="h-[500px] pr-4">
                  <div className="space-y-4">
                    {activities.map((activity, index) => {
                      const ActivityIcon = getActivityIcon(activity.type);
                      const typeLabel = ACTIVITY_TYPES.find(t => t.value === activity.type)?.label || activity.type;
                      return (
                        <div key={activity.id || index} className="relative pl-6 pb-4 border-l-2 border-muted last:border-0">
                          <div className="absolute -left-2 top-0 w-4 h-4 rounded-full bg-accent flex items-center justify-center">
                            <ActivityIcon className="w-2.5 h-2.5 text-white" />
                          </div>
                          <div className="bg-muted/50 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-sm">{typeLabel}</span>
                              <span className="text-xs text-muted-foreground">
                                {new Date(activity.created_at).toLocaleDateString("it-IT", {
                                  day: "2-digit",
                                  month: "short",
                                  hour: "2-digit",
                                  minute: "2-digit"
                                })}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground">{activity.description}</p>
                            {activity.created_by_name && (
                              <p className="text-xs text-muted-foreground mt-2">— {activity.created_by_name}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add Activity Dialog */}
      <Dialog open={activityDialogOpen} onOpenChange={setActivityDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registra Attività</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Tipo di Attività</Label>
              <Select
                value={newActivity.type}
                onValueChange={(value) => setNewActivity({ ...newActivity, type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACTIVITY_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Textarea
                rows={3}
                placeholder="Descrivi l'attività..."
                value={newActivity.description}
                onChange={(e) => setNewActivity({ ...newActivity, description: e.target.value })}
              />
            </div>
            <Button onClick={handleAddActivity} className="w-full">
              Salva Attività
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Invia Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm"><strong>Destinatario:</strong> {lead.email}</p>
              <p className="text-sm"><strong>Azienda:</strong> {lead.company_name}</p>
            </div>
            
            <div className="space-y-2">
              <Label>Lingua</Label>
              <Select value={emailLanguage} onValueChange={setEmailLanguage}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="it">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Italiano
                    </div>
                  </SelectItem>
                  <SelectItem value="en">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      English
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Template Email</Label>
              {templates.length === 0 ? (
                <p className="text-sm text-muted-foreground p-3 bg-muted rounded">
                  Nessun template disponibile. Vai su Impostazioni → Template Email per crearne uno.
                </p>
              ) : (
                <Select value={selectedTemplate || ""} onValueChange={setSelectedTemplate}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona un template..." />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        <div className="flex items-center gap-2">
                          {template.type === "presentation" && <Send className="w-4 h-4 text-blue-500" />}
                          {template.type === "catalog" && <FileText className="w-4 h-4 text-purple-500" />}
                          {template.type === "pricelist" && <Euro className="w-4 h-4 text-green-500" />}
                          {template.type === "other" && <Mail className="w-4 h-4 text-gray-500" />}
                          {template.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {selectedTemplate && (
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm">
                <p className="font-medium text-blue-700 dark:text-blue-300">
                  Anteprima template selezionato
                </p>
                {(() => {
                  const tpl = templates.find(t => t.id === selectedTemplate);
                  if (!tpl) return null;
                  const content = emailLanguage === "en" ? tpl.content_en : tpl.content_it;
                  return (
                    <div className="mt-2 text-muted-foreground">
                      <p><strong>Oggetto:</strong> {emailLanguage === "en" ? tpl.subject_en : tpl.subject_it}</p>
                      <p className="mt-1 line-clamp-3">{content?.substring(0, 200)}...</p>
                      {tpl.attachments && tpl.attachments.length > 0 && (
                        <p className="mt-1"><strong>Allegati:</strong> {tpl.attachments.length} file</p>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            <Button 
              onClick={handleSendEmail} 
              className="w-full" 
              disabled={!selectedTemplate || sendingEmail}
            >
              {sendingEmail ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Invio in corso...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Invia Email
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Lead Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Modifica Lead</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto p-1">
            <div className="space-y-2">
              <Label>Azienda</Label>
              <Input
                value={editForm.company_name || ""}
                onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Referente</Label>
              <Input
                value={editForm.contact_name || ""}
                onChange={(e) => setEditForm({ ...editForm, contact_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={editForm.email || ""}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Telefono</Label>
              <Input
                value={editForm.phone || ""}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Ruolo</Label>
              <Input
                value={editForm.contact_role || ""}
                onChange={(e) => setEditForm({ ...editForm, contact_role: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Canale</Label>
              <Select
                value={editForm.channel || "Retail"}
                onValueChange={(value) => setEditForm({ ...editForm, channel: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[
                    { value: "Retail", label: "Retail / Normal Trade" },
                    { value: "GDO", label: "GDO" },
                    { value: "DO", label: "DO" },
                    { value: "Ho.Re.Ca.", label: "Ho.Re.Ca." },
                    { value: "Distributore", label: "Distributore" },
                  ].map((ch) => (
                    <SelectItem key={ch.value} value={ch.value}>{ch.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Note</Label>
              <Textarea
                rows={3}
                value={editForm.notes || ""}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleUpdateLead}>
              Salva Modifiche
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
